import {
  Component,
  useEffect,
  useId,
  useMemo,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router";
import {
  IconButton,
  Modal,
  MoonIcon,
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
  PlugIcon,
  Scrollbar,
  SunIcon,
  Text,
  Tooltip,
  TooltipIconButton,
} from "@carbon6/c6-frontend-design";
import { McpConnectPanel } from "@/modules/mcp/public";
import { toErrorLogContext } from "@/shared/http";
import { createLogger } from "@/shared/logging";
import { useAppState } from "../state/AppStateProvider";
import { appTextModes, useThemeModePreference } from "../theme";
import { Gnb, type GnbMenu } from "./Gnb";
import { AppUpdateBanner } from "./AppUpdateBanner";
import { ServerInfoIndicator } from "./ServerInfoIndicator";
import { SidebarAccountMenu } from "./SidebarAccountMenu";
import styles from "./PageBase.module.css";

const logger = createLogger("app/shell/pageBase");
const sidebarCollapsedStorageKey = "spawning-pool:sidebar-collapsed";
const sidebarCollapseMediaQuery = "(min-width: 561px)";

export type PageBaseProps = {
  pageTitle: string;
  // 제목 hover 시 오른쪽에 뜨는 안내 문장. 비우면 아무것도 그리지 않는다.
  pageTitleTooltip?: string;
  currentMenu: GnbMenu;
  showBackButton?: boolean;
  scrollable?: boolean;
  tabs?: PageBaseTabs;
  // 탭이 제목 자리를 차지한다. 제목 텍스트 대신 큰 탭 줄을 그리고 아래 탭 줄은 두지 않는다.
  // 제목이 첫 탭과 같은 말을 반복하는 페이지용이며, pageTitle 은 화면에서 빠져도 페이지의
  // 접근 이름으로 남는다.
  tabsReplaceTitle?: boolean;
  // 탭 줄 아래·본문 위에 서는 페이지 슬롯(탭과 콘텐츠 사이). header 안·본문(tabpanel)
  // 밖이라 탭 전환과 무관하게 남는다 — 모든 탭에 공통으로 걸리는 조건이 자리다.
  // 인스턴스 상태(컨트롤러·소스)로 내용을 그려야 하는 페이지는 prop 대신 renderBelowTabs(context) 를
  // 오버라이드한다.
  belowTabs?: ReactNode;
  // 제목 텍스트 바로 옆(왼쪽 정렬)에 서는 자리 — "이 화면이 무엇을 보고 있는가"에 붙는 것이
  // 산다(스코프 컨트롤·읽기 전용 상태 배지). 누를 수 있으면 headerActions, 읽기만 하면 여기다.
  titleAside?: ReactNode;
  // 제목 우측 끝(탭 위)에 항상 표시되는 페이지 액션. 탭 전환과 무관하게 헤더에 고정된다.
  headerActions?: ReactNode;
  // 본문(tabpanel) 상단 padding 을 끈다 — belowTabs 필터 블록이 이미 헤더·본문 경계를
  // 만드는 페이지가 켠다.
  bodyFlushTop?: boolean;
};

export type PageBaseTab = {
  value: string;
  label: ReactNode;
  disabled?: boolean;
};

export type PageBaseTabs = {
  items: readonly PageBaseTab[];
  value?: string;
  defaultValue?: string;
  ariaLabel?: string;
  onValueChange?: (value: string) => void;
};

export type PageBaseContext = {
  currentUserName: string;
  // Overlord identity subject(미인증이면 null). subject 스코프 localStorage 키 등에 쓴다.
  currentUserSubject: string | null;
  currentTab: string | null;
};

export abstract class PageBase<
  Props extends PageBaseProps = PageBaseProps,
  State = object,
> extends Component<Props, State> {
  componentDidMount(): void {
    // 페이지 진입 직후 필요한 비동기 초기화를 각 페이지가 구현한다.
    void Promise.resolve(this.initPage()).catch((error: unknown) => {
      logger.error("initPage failed.", toErrorLogContext(error));
    });
  }

  componentWillUnmount(): void {
    // 라우트 이탈 시 페이지 단위 구독/캐시를 정리한다.
    void Promise.resolve(this.disposePage()).catch((error: unknown) => {
      logger.error("disposePage failed.", toErrorLogContext(error));
    });
  }

  protected initPage(): void | Promise<void> {
    return undefined;
  }

  protected disposePage(): void | Promise<void> {
    return undefined;
  }

  protected abstract renderContent(context: PageBaseContext): ReactNode;

  // 제목 옆 스코프 슬롯 — renderContent 와 같은 컨텍스트 훅. 기본은 titleAside prop 을 그대로 쓴다.
  protected renderTitleAside(_context: PageBaseContext): ReactNode {
    return this.props.titleAside ?? null;
  }

  // 제목 우측 헤더 액션 — renderContent 와 같은 컨텍스트 훅. 기본은 headerActions prop 을 그대로 쓴다.
  protected renderHeaderActions(_context: PageBaseContext): ReactNode {
    return this.props.headerActions ?? null;
  }

  // 탭 줄 아래 페이지 슬롯 — 기본은 belowTabs prop 을 그대로 쓰고, 인스턴스 상태를 그려야 하는
  // 페이지가 오버라이드한다.
  protected renderBelowTabs(_context: PageBaseContext): ReactNode {
    return this.props.belowTabs ?? null;
  }

  render(): ReactNode {
    const {
      bodyFlushTop = false,
      currentMenu,
      pageTitle,
      pageTitleTooltip,
      scrollable = false,
      showBackButton = false,
      tabs,
      tabsReplaceTitle,
    } = this.props;

    return (
      <PageBaseLayout
        bodyFlushTop={bodyFlushTop}
        currentMenu={currentMenu}
        pageTitle={pageTitle}
        pageTitleTooltip={pageTitleTooltip}
        scrollable={scrollable}
        showBackButton={showBackButton}
        tabs={tabs}
        tabsReplaceTitle={tabsReplaceTitle}
        belowTabs={(context) => this.renderBelowTabs(context)}
        titleAside={(context) => this.renderTitleAside(context)}
        headerActions={(context) => this.renderHeaderActions(context)}
      >
        {(context) => this.renderContent(context)}
      </PageBaseLayout>
    );
  }
}

// 레이아웃은 컨텍스트(현재 탭)가 확정된 뒤 헤더 액션·탭 위 슬롯을 평가한다 — prop 노드와 달리
// 렌더 시점의 최신 상태를 읽을 수 있다.
type PageBaseLayoutProps = Omit<PageBaseProps, "headerActions" | "belowTabs" | "titleAside"> & {
  belowTabs: (context: PageBaseContext) => ReactNode;
  titleAside: (context: PageBaseContext) => ReactNode;
  headerActions: (context: PageBaseContext) => ReactNode;
  children: (context: PageBaseContext) => ReactNode;
};

const MCP_CONNECT_LABEL = "MCP 연결";

function PageBaseLayout({
  bodyFlushTop = false,
  children,
  currentMenu,
  pageTitle,
  pageTitleTooltip,
  scrollable = false,
  showBackButton,
  tabs,
  tabsReplaceTitle = false,
  belowTabs,
  titleAside,
  headerActions,
}: PageBaseLayoutProps) {
  const { state, endSession } = useAppState();
  const navigate = useNavigate();
  const session = state.auth.session;
  const [sidebarCollapsed, setSidebarCollapsed] = useSidebarCollapsedPreference();
  const sidebarCanCollapse = useSidebarCollapseEnabled();
  const effectiveSidebarCollapsed = sidebarCanCollapse && sidebarCollapsed;
  const [themeMode, setThemeMode] = useThemeModePreference();
  const [mcpConnectOpen, setMcpConnectOpen] = useState(false);
  const tabState = usePageBaseTabs(tabs);
  const hasTabs = tabs != null && tabs.items.length > 0;
  const tabIdPrefix = useId();
  const tabPanelId = `${tabIdPrefix}-panel`;
  const selectedTabIndex =
    hasTabs && tabState.selectedValue != null
      ? tabs.items.findIndex((tab) => tab.value === tabState.selectedValue)
      : -1;
  const selectedTabId =
    selectedTabIndex >= 0 ? `${tabIdPrefix}-tab-${selectedTabIndex}` : undefined;
  const bodyRole = hasTabs ? "tabpanel" : undefined;
  // 본문 상단 여백을 페이지가 끈 경우(belowTabs 가 이미 경계를 만드는 화면) flush 클래스를 얹는다.
  const bodyClassName = bodyFlushTop ? `${styles.body} ${styles.bodyFlushTop}` : styles.body;
  // 탭이 있어야 제목을 대신할 수 있다. 탭이 없으면 제목 없는 헤더가 되므로 기본 모드로 돌아간다.
  const tabsInTitleRow = tabsReplaceTitle && hasTabs;
  // 제목 줄과 그 아래 줄 중 한 곳에만 놓이는 같은 탭 줄이다.
  const tabsNode = hasTabs ? (
    <PageTabs
      tabs={tabs}
      selectedValue={tabState.selectedValue}
      idPrefix={tabIdPrefix}
      panelId={tabPanelId}
      large={tabsInTitleRow}
      onSelect={tabState.selectValue}
    />
  ) : null;
  const context = useMemo<PageBaseContext>(
    () => ({
      currentUserName: session?.displayName ?? "사용자",
      currentUserSubject: session?.subject ?? null,
      currentTab: hasTabs ? tabState.selectedValue : null,
    }),
    [session, hasTabs, tabState.selectedValue],
  );
  const titleAsideNode = titleAside(context);
  const headerActionsNode = headerActions(context);
  const belowTabsNode = belowTabs(context);

  function handleSidebarToggle() {
    setSidebarCollapsed((collapsed) => !collapsed);
  }

  function handleLogout() {
    endSession();
    navigate("/", { replace: true });
  }

  const nextThemeMode = themeMode === "dark" ? "light" : "dark";
  const themeToggleLabel = nextThemeMode === "dark" ? "다크 모드로 전환" : "라이트 모드로 전환";
  const themeToggleIcon = nextThemeMode === "dark" ? <MoonIcon /> : <SunIcon />;

  // 계정 메뉴 — 펼침에선 브랜드 타이틀 옆 chevron, 접힘에선 사이드바 맨 아래 브랜드 마크 트리거.
  const accountMenu = (
    <SidebarAccountMenu
      collapsed={effectiveSidebarCollapsed}
      userName={context.currentUserName}
      username={session?.username ?? null}
      organizationName={state.auth.membership?.organizationName ?? null}
      onLogout={handleLogout}
    />
  );

  return (
    <main
      className={styles.shell}
      data-sidebar-collapsed={effectiveSidebarCollapsed ? "true" : "false"}
    >
      <aside
        id="page-base-sidebar"
        className={styles.sidebar}
        aria-label="전역 내비게이션"
        data-collapsed={effectiveSidebarCollapsed ? "true" : "false"}
      >
        <div className={styles.sidebarMain}>
          <Gnb
            collapsed={effectiveSidebarCollapsed}
            currentMenu={currentMenu}
            serverContext={<ServerInfoIndicator collapsed={effectiveSidebarCollapsed} />}
            banner={<AppUpdateBanner collapsed={effectiveSidebarCollapsed} />}
            // 펼침에선 타이틀 옆에 계정 chevron. 접힘에선 아래 sidebarBottom 으로 옮긴다.
            brandMenu={effectiveSidebarCollapsed ? undefined : accountMenu}
            // 브랜드 행 우측 — 사이드바 collapse 토글.
            collapseControl={
              sidebarCanCollapse ? (
                <TooltipIconButton
                  tooltip={{
                    content: effectiveSidebarCollapsed ? "사이드바 펼치기" : "사이드바 접기",
                    placement: "right",
                  }}
                  size="md"
                  tone="neutral"
                  variant="ghost"
                  onClick={handleSidebarToggle}
                  aria-controls="page-base-sidebar"
                  aria-expanded={!effectiveSidebarCollapsed}
                >
                  {effectiveSidebarCollapsed ? <PanelLeftOpenIcon /> : <PanelLeftCloseIcon />}
                </TooltipIconButton>
              ) : undefined
            }
          />
        </div>
        {/* 사이드바 맨 아래 고정 — MCP 연결, 테마 토글, 접힘에서 내려오는 계정 트리거 순.
            접힘 레일엔 텍스트가 들어가지 않아 같은 자리를 아이콘 버튼이 대신한다. */}
        <div className={styles.sidebarBottom}>
          {effectiveSidebarCollapsed ? (
            <TooltipIconButton
              tooltip={{ content: MCP_CONNECT_LABEL, placement: "right" }}
              size="md"
              tone="neutral"
              variant="ghost"
              onClick={() => setMcpConnectOpen(true)}
            >
              <PlugIcon />
            </TooltipIconButton>
          ) : (
            <SidebarTextButton
              icon={<PlugIcon />}
              label={MCP_CONNECT_LABEL}
              onClick={() => setMcpConnectOpen(true)}
            />
          )}
          {effectiveSidebarCollapsed ? (
            <TooltipIconButton
              tooltip={{ content: themeToggleLabel, placement: "right" }}
              size="md"
              tone="neutral"
              variant="ghost"
              onClick={() => setThemeMode(nextThemeMode)}
            >
              {themeToggleIcon}
            </TooltipIconButton>
          ) : (
            <SidebarTextButton
              icon={themeToggleIcon}
              label={themeToggleLabel}
              onClick={() => setThemeMode(nextThemeMode)}
            />
          )}
          {effectiveSidebarCollapsed ? accountMenu : null}
        </div>
      </aside>
      <section className={styles.content} aria-label={pageTitle}>
        <header className={styles.header}>
          <div className={styles.headerTop}>
            <div className={styles.titleGroup}>
              {showBackButton ? <PageBackButton /> : null}
              {tabsInTitleRow ? (
                tabsNode
              ) : pageTitleTooltip ? (
                // 커스텀 툴팁이 native title 과 겹쳐 뜨지 않게 title 속성은 뺀다.
                <Tooltip content={pageTitleTooltip} placement="right">
                  <Text variant={appTextModes["head-bold"]}>{pageTitle}</Text>
                </Tooltip>
              ) : (
                <Text variant={appTextModes["head-bold"]} title={pageTitle}>
                  {pageTitle}
                </Text>
              )}
              {titleAsideNode ? <div className={styles.titleAside}>{titleAsideNode}</div> : null}
            </div>
            {headerActionsNode ? (
              <div className={styles.headerActions}>{headerActionsNode}</div>
            ) : null}
          </div>
          {tabsInTitleRow ? null : tabsNode}
          {belowTabsNode ? <div className={styles.belowTabs}>{belowTabsNode}</div> : null}
        </header>
        {scrollable ? (
          <Scrollbar
            className={bodyClassName}
            gutter="auto"
            data-route-scroll-container="true"
            id={hasTabs ? tabPanelId : undefined}
            role={bodyRole}
            aria-labelledby={selectedTabId}
          >
            {children(context)}
          </Scrollbar>
        ) : (
          <div
            className={bodyClassName}
            id={hasTabs ? tabPanelId : undefined}
            role={bodyRole}
            aria-labelledby={selectedTabId}
          >
            {children(context)}
          </div>
        )}
      </section>
      {/* 열 때만 마운트한다. 닫으면 발급한 연결 코드와 카운트다운을 버린다. */}
      {mcpConnectOpen ? (
        <Modal open size="md" onClose={() => setMcpConnectOpen(false)}>
          <Modal.Header>
            <Modal.Header.Start>
              <Modal.Header.Title>{MCP_CONNECT_LABEL}</Modal.Header.Title>
            </Modal.Header.Start>
            <Modal.Header.End>
              <Modal.Header.Close label="닫기" />
            </Modal.Header.End>
          </Modal.Header>
          <Modal.Body>
            <McpConnectPanel />
          </Modal.Body>
        </Modal>
      ) : null}
    </main>
  );
}

type PageBaseTabsState = {
  selectedValue: string | null;
  selectValue: (value: string) => void;
};

function usePageBaseTabs(tabs: PageBaseTabs | undefined): PageBaseTabsState {
  const [uncontrolledValue, setUncontrolledValue] = useState<string | null>(() =>
    normalizePageBaseTabValue(tabs, tabs?.defaultValue ?? null),
  );
  const selectedValue = normalizePageBaseTabValue(
    tabs,
    tabs?.value ?? uncontrolledValue ?? tabs?.defaultValue ?? null,
  );

  useEffect(() => {
    if (tabs == null || tabs.value !== undefined || selectedValue === uncontrolledValue) {
      return;
    }

    setUncontrolledValue(selectedValue);
  }, [selectedValue, tabs, uncontrolledValue]);

  function selectValue(value: string) {
    if (tabs == null || !isSelectablePageBaseTabValue(tabs, value) || value === selectedValue) {
      return;
    }

    if (tabs.value === undefined) {
      setUncontrolledValue(value);
    }

    tabs.onValueChange?.(value);
  }

  return { selectedValue, selectValue };
}

function normalizePageBaseTabValue(
  tabs: PageBaseTabs | undefined,
  value: string | null,
): string | null {
  if (tabs == null) {
    return null;
  }

  const enabledItems = tabs.items.filter((tab) => !tab.disabled);
  if (enabledItems.length === 0) {
    return null;
  }

  if (value != null && enabledItems.some((tab) => tab.value === value)) {
    return value;
  }

  return enabledItems[0]?.value ?? null;
}

function isSelectablePageBaseTabValue(tabs: PageBaseTabs, value: string) {
  return tabs.items.some((tab) => tab.value === value && !tab.disabled);
}

type PageTabsProps = {
  tabs: PageBaseTabs;
  selectedValue: string | null;
  idPrefix: string;
  panelId: string;
  // 제목 자리에 선 탭 줄 — 제목이 있던 크기감을 탭이 대신 가진다.
  large?: boolean;
  onSelect: (value: string) => void;
};

function PageTabs({ tabs, selectedValue, idPrefix, panelId, large, onSelect }: PageTabsProps) {
  return (
    <div
      className={large ? `${styles.tabs} ${styles.tabsLarge}` : styles.tabs}
      role="tablist"
      aria-label={tabs.ariaLabel ?? "페이지 섹션"}
      tabIndex={-1}
      onKeyDown={(event) => handlePageTabsKeyDown(event, tabs.items, selectedValue, onSelect)}
    >
      {tabs.items.map((tab, index) => {
        const selected = tab.value === selectedValue;

        return (
          <button
            key={tab.value}
            id={`${idPrefix}-tab-${index}`}
            className={styles.tab}
            type="button"
            role="tab"
            aria-controls={panelId}
            aria-selected={selected}
            disabled={tab.disabled}
            tabIndex={selected ? 0 : -1}
            onClick={() => onSelect(tab.value)}
          >
            <span className={styles.tabLabel}>{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function handlePageTabsKeyDown(
  event: KeyboardEvent<HTMLDivElement>,
  items: readonly PageBaseTab[],
  selectedValue: string | null,
  onSelect: (value: string) => void,
) {
  const enabledItems = items.filter((tab) => !tab.disabled);
  if (enabledItems.length === 0) {
    return;
  }

  const selectedIndex = Math.max(
    0,
    enabledItems.findIndex((tab) => tab.value === selectedValue),
  );
  let nextIndex: number;

  switch (event.key) {
    case "ArrowRight":
      nextIndex = (selectedIndex + 1) % enabledItems.length;
      break;
    case "ArrowLeft":
      nextIndex = (selectedIndex - 1 + enabledItems.length) % enabledItems.length;
      break;
    case "Home":
      nextIndex = 0;
      break;
    case "End":
      nextIndex = enabledItems.length - 1;
      break;
    default:
      return;
  }

  event.preventDefault();
  const enabledButtons = Array.from(
    event.currentTarget.querySelectorAll<HTMLButtonElement>('button[role="tab"]:not(:disabled)'),
  );
  enabledButtons[nextIndex]?.focus();
  onSelect(enabledItems[nextIndex].value);
}

function PageBackButton() {
  const navigate = useNavigate();

  return (
    <IconButton
      aria-label="뒤로"
      className={styles.backButton}
      size="md"
      title="이전 페이지로 이동합니다."
      variant="surface"
      onClick={() => navigate(-1)}
    >
      <BackIcon />
    </IconButton>
  );
}

function BackIcon() {
  return (
    <svg className={styles.backIcon} aria-hidden="true" viewBox="0 0 16 16">
      <path d="M9.75 3.5 5.25 8l4.5 4.5" />
      <path d="M5.75 8h7" />
    </svg>
  );
}

// 사이드바 텍스트 버튼 — 라벨 왼쪽에 아이콘. 라벨이 곧 설명이라 아이콘 버튼과 달리 툴팁을 달지 않는다.
function SidebarTextButton({
  icon,
  label,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
}) {
  return (
    <button className={styles.sidebarTextButton} type="button" onClick={onClick}>
      <span className={styles.sidebarTextButtonIcon} aria-hidden="true">
        {icon}
      </span>
      {label}
    </button>
  );
}

function useSidebarCollapsedPreference() {
  const [collapsed, setCollapsed] = useState(() => readStoredSidebarCollapsed());

  useEffect(() => {
    writeStoredSidebarCollapsed(collapsed);
  }, [collapsed]);

  return [collapsed, setCollapsed] as const;
}

function useSidebarCollapseEnabled() {
  const [enabled, setEnabled] = useState(() => readSidebarCollapseMediaQuery());

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(sidebarCollapseMediaQuery);
    const handleChange = () => setEnabled(mediaQuery.matches);

    handleChange();
    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return enabled;
}

function readSidebarCollapseMediaQuery() {
  if (typeof window === "undefined") {
    return true;
  }

  return window.matchMedia(sidebarCollapseMediaQuery).matches;
}

function readStoredSidebarCollapsed() {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    return window.localStorage.getItem(sidebarCollapsedStorageKey) === "true";
  } catch {
    return false;
  }
}

function writeStoredSidebarCollapsed(collapsed: boolean) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(sidebarCollapsedStorageKey, collapsed ? "true" : "false");
  } catch {
    // Storage can be unavailable in private or restricted browser contexts.
  }
}
